import React, { useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import { t } from '@lingui/macro';
import {
  CurrentAssigned,
  HeaderContainer,
  StudentHeader,
  StudentListItemWrapper,
  StudentListWrapper,
  SvgWrapper
} from './styled';

import { customSort } from './student-list-util';
import StudentListItem from './student-list-item';
import { DownArrow, UpArrow } from '../../helpers/icon-dictionary';
import SvgIcon from '../svg-icon/svg-icon';
import QuickSelector from '../quick-selector/quick-selector';
import {
  UPDATE_SORT_PROPERTIES,
  UPDATE_SORTED_STUDENTS_LIST
} from '../../containers/course-assign/course-assign-types';
import { isDefined } from '../../helpers/utils';

const StudentList = ({ i18n }) => {
  const sortedStudents = useSelector(
    (state) => state.CourseAssignReducer.sortedStudents,
    shallowEqual
  );

  const mappedAssignmentsSelector = createSelector(
    (state) => state.CourseAssignReducer?.assignments?.studentAssignments,
    (studentAssignments) => {
      const userObj = {};
      studentAssignments.forEach((studentAssignment, index) => {
        const userArr = userObj[studentAssignment.encryptedUserProfileId];
        if (isDefined(userArr)) {
          userObj[studentAssignment.encryptedUserProfileId].push(index);
        } else {
          userObj[studentAssignment.encryptedUserProfileId] = [index];
        }
      });
      return userObj;
    }
  );

  const mappedAssignmentIndexes = useSelector(
    mappedAssignmentsSelector,
    shallowEqual
  );

  const sortDirection = useSelector(
    (state) => state.CourseAssignReducer.sortDirection
  );

  const isLoading = useSelector((state) => state.CourseAssignReducer.isLoading);

  const totalAssignments = useSelector(
    (state) => state.CourseAssignReducer.assignments.totalAssignments,
    shallowEqual
  );

  const dispatch = useDispatch();

  const nameLabel = `${i18n._(t('studentList.students')`Students`)} (${
    sortedStudents.length || 0
  })`;

  const sortHandler = () => {
    const updatedSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';

    dispatch({
      type: UPDATE_SORT_PROPERTIES,
      direction: updatedSortDirection,
      sortValue: 'lastName'
    });

    const sortedList = customSort(
      sortedStudents,
      'lastName',
      updatedSortDirection
    );

    dispatch({
      type: UPDATE_SORTED_STUDENTS_LIST,
      students: sortedList
    });
  };

  const handleSortingArrow = (dir) => {
    const icon =
      dir === 'asc' ? <SvgIcon icon={DownArrow} /> : <SvgIcon icon={UpArrow} />;
    return <SvgWrapper status>{icon}</SvgWrapper>;
  };

  const studentList = useMemo(
    () =>
      sortedStudents.map((student) => {
        const filteredStudent =
          totalAssignments &&
          totalAssignments.filter(
            (assignments) =>
              assignments.encryptedUserProfileId ===
              student.encryptedUserProfileId
          );
        const assignmentCount =
          filteredStudent?.length && filteredStudent[0].assignedActivityTotal;

        const dueDate = filteredStudent?.[0]?.nextDueDate;

        return (
          <StudentListItem
            i18n={i18n}
            assignmentCount={assignmentCount}
            dueDate={dueDate}
            student={student}
            mappedAssignmentIndexes={
              mappedAssignmentIndexes?.[student.encryptedUserProfileId] || []
            }
            key={`_${student.encryptedUserProfileId}`}
          />
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedStudents, dispatch, i18n, totalAssignments]
  );

  if (isLoading) return <div />;

  return (
    <StudentListWrapper id="studentList">
      <HeaderContainer>
        <StudentHeader>
          <span
            className="title"
            role="button"
            tabIndex="-1"
            onClick={sortHandler}
            data-testid="toggle-sort"
          >
            {handleSortingArrow(sortDirection)}
            <span className="label" data-testid="name-label">
              {nameLabel}
            </span>
          </span>
          <CurrentAssigned data-testid="assigned-label">
            <span>
              {i18n._(t('studentList.currentlyAssigned')`Currently Assigned`)}
            </span>
          </CurrentAssigned>
        </StudentHeader>
        <QuickSelector i18n={i18n} />
      </HeaderContainer>
      <StudentListItemWrapper>{studentList}</StudentListItemWrapper>
    </StudentListWrapper>
  );
};

StudentList.propTypes = {
  i18n: PropTypes.object
};

StudentList.whyDidYouRender = false;

export default StudentList;



// WEBPACK FOOTER //
// ./components/student-list/student-list.js